// G-BRANDLOGO-01 — hydrate the brand (company) logo for the session, in ONE place.
//
// Why: since G-UPLOAD-01 the PERSISTED companyLogo value is a stable OCI storage KEY
// (e.g. tenants/<id>/.../logo.png), which must be resolved into a short-lived presigned URL via
// POST /documents/access every session (storageAssetService.resolveAssetUrl). The enterprise
// login response carries no logo at all, so the login-time hydration paths (Signin/SSOCallback/
// Updatepassword) write ""/the raw key into localStorage BRAND_LOGO and the header/sidebar logo
// stayed broken until the user visited Company Details. Resolving here — on mount of the
// authenticated layout (Layouts.tsx) — covers every entry path with one hook: fresh password
// login, SSO callback, forced password update, the supplier-portal session (same layout), and
// stale sessions whose stored presigned URL has expired.
//
// UAT-3: never leave the header logo blank or invisible. The resolved URL is probed with an
// Image() before being trusted — broken loads (naturalWidth 0) and degenerate placeholder
// images (e.g. the 1x1 transparent PNG a Wave-2 e2e once persisted as the team logo) fall back
// to the bundled EZ Cloud logo, as does "no logo configured" and any fetch failure.

import { useEffect } from "react";
import { useAppDispatch } from "../redux/hooks";
import { UPDATE_COMPANY_LOGO } from "../redux/action/ActionType";
import API from "../constants/API";
import { authHeader } from "../common/mainfunction";
import { GET_TEAMDETAILS_BY_ID } from "../common/url";
import { getTeamID } from "../common/function";
import { resolveAssetUrl } from "../services/storageAssetService";
import DefaultBrandLogo from "../images/Logo_Color1.png";

// Probe whether `url` actually renders as a visible image. Resolves false for load errors
// (naturalWidth 0), degenerate ≤2px placeholders, and anything that hasn't loaded within the
// timeout (also keeps jsdom/test runs from hanging — Image() never fires events there).
function isVisiblyRenderable(url: string, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(false), timeoutMs);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img.naturalWidth > 2 && img.naturalHeight > 2);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    img.src = url;
  });
}

export default function useBrandLogoHydration(): void {
  const dispatch = useAppDispatch();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only for authenticated sessions (Layouts is auth-gated, but be defensive).
      if (!localStorage.getItem("AUTH_TOKEN")) return;
      let renderableUrl = "";
      try {
        const response = await API({
          method: "GET",
          url: GET_TEAMDETAILS_BY_ID,
          headers: authHeader(),
          params: { teamId: getTeamID() },
        });
        const { status, data } = (response.data ?? {}) as {
          status?: string;
          data?: { companyLogo?: string | null }[];
        };
        if (status === "Success") {
          const storedValue = data?.[0]?.companyLogo ?? "";
          // Key -> fresh presigned URL ("" when no logo or on failure). Legacy absolute URLs pass
          // through unchanged inside resolveAssetUrl.
          renderableUrl = await resolveAssetUrl(storedValue);
        }
      } catch {
        // Non-fatal: fall through to the default logo below.
      }
      const finalUrl =
        renderableUrl && (await isVisiblyRenderable(renderableUrl))
          ? renderableUrl
          : DefaultBrandLogo;
      if (cancelled) return;
      localStorage.setItem("BRAND_LOGO", finalUrl);
      dispatch({ type: UPDATE_COMPANY_LOGO, payload: finalUrl });
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);
}
